import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useGameConfig } from '../../config/GameConfigProvider';
import { DeviceInfo } from '../../deviceInfo/deviceInfo';
import { useLobbyURLConnected } from '../../hooks/useLobbyURLConnected';
import { useSlotsRedirectFunction } from '../../hooks/useSlotsRedirectFunction';
import { CoreLobbyMessageController } from '../../messages/CoreLobbyMessageController';
import { useUpdateBalanceMessageCallbackConnected } from '../../messages/messages/updateBalance/useUpdateBalance';
import { useCoreDispatch } from '../../store/CoreStore';
import { useConfigSlice } from '../../store/slices/config/selectors';
import { gameStateActions } from '../../store/slices/game/slice';
import { useHeaderSlice } from '../../store/slices/header/selectors';
import { headerActions } from '../../store/slices/header/slice';
import { useMiniLobbySlice } from '../../store/slices/minilobby/selectors';
import { useModifiers } from '../../styles/hooks/useModifiers';
import { isCurrentBrowserIOS } from '../../utils/browsers';
import { URLUtilities } from '../../utils/url';
import { IconButton, IconButtonSize, Icons } from '../controls/iconButton/IconButton';

import { detectPortrait, generateLobbyUrl } from './utils';

import styles from './LobbyConnected.module.css';

const isProduction = process.env.NODE_ENV === 'production';

export const LobbyBodyConnected = (): ReactElement | null => {
  /** URL configuration starts here **/
  const session = useConfigSlice.session();
  const env = useConfigSlice.env();
  const tableConfig = useConfigSlice.tableConfig();
  const lobbyFolder = useLobbyURLConnected({ folderOnly: true });
  const isIframeLoaded = useMiniLobbySlice.getIsIframeLoaded();

  const JSESSIONID = session?.JSESSIONID ?? '';
  const casino_id = session?.casinoId ?? '';
  const web_server = session?.webServer ?? '';
  const config_url = session?.configUrl ?? '';
  const socket_server = session?.socketServer ?? '';
  const actual_web_server = session?.actualWebServer ?? '';
  const lang = session?.lang ?? '';
  const stats_collector_uuid = session?.statsCollectorUuid ?? '';
  const socket_port = session?.socketPort ?? '';
  const uiversion = session?.uiversion ?? '';
  const userId = session?.userId ?? '';
  const meta_server = session?.metaServer ?? '';
  const common_components_theme = session?.commonComponentsTheme ?? '';
  const referer = session?.tableId ?? '';

  const operator_theme = tableConfig?.operatorTheme ?? '';
  const playerUI = env?.playerUI ?? '';

  let uiAddress = URLUtilities.combine(playerUI, `${lobbyFolder}/`).toString();

  if (!isProduction) {
    uiAddress = '/proxy/' + uiAddress;
  }

  const searchParams = useMemo(() => {
    return new URLSearchParams({
      JSESSIONID,
      actual_web_server,
      casino_id,
      config_url,
      game_mode: 'html5_desktop',
      gametype: 'all',
      lang,
      meta_server,
      nocache: '1',
      operator_theme,
      socket_port,
      socket_server,
      stats_collector_uuid,
      swf_lobby_path: '/member/games/lobby.swf',
      timeStamp: String(Date.now()),
      token: JSESSIONID,
      uiAddress,
      uiversion,
      userId,
      web_server,
      common_components_theme,
      hostCommunicationVersion: 'v2',
      referer,
    });
  }, [
    JSESSIONID,
    actual_web_server,
    casino_id,
    config_url,
    lang,
    meta_server,
    operator_theme,
    socket_port,
    socket_server,
    stats_collector_uuid,
    uiAddress,
    uiversion,
    userId,
    web_server,
    common_components_theme,
    referer,
  ]);

  const LOBBY_URL = generateLobbyUrl(uiAddress, searchParams);
  /** URL configuration ends here **/

  const iframeWrapperRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [showMiniLobbyCloseButton, setShowMiniLobbyCloseButton] = useState(false);

  useEffect(() => {
    if (!isIframeLoaded) {
      const timeoutId = setTimeout(() => {
        setShowMiniLobbyCloseButton(true);
      }, 3000);
      return (): void => clearTimeout(timeoutId);
    } else {
      setShowMiniLobbyCloseButton(false);
    }
  }, [isIframeLoaded]);

  // Detect iframe content and gracefully handle CORS errors preventing hitting error boundary
  useEffect(() => {
    try {
      if (iframeRef.current) {
        iframeRef.current.addEventListener('load', () => {
          if (!iframeRef.current) return;
          iframeRef.current.classList.add(styles.visible);
        });
      }
    } catch (error) {
      console.error('Error accessing iframe content:', error);
    }
  }, [isIframeLoaded]);

  const updateBalance = useUpdateBalanceMessageCallbackConnected();
  // fixme: This does not seem to update balance in mini lobby. Better to move to handleMessageFromIframe() if should be done once on iframe load
  useEffect(() => {
    if (!isIframeLoaded) return;
    updateBalance();
  }, [isIframeLoaded, updateBalance]);

  const dispatch = useCoreDispatch();

  // iOS 17.1 fix: simulate user interaction to force viewport recalculation after rotation (LCE-63290).
  const handleOrientationChange = useCallback(() => {
    const isPortrait = detectPortrait();

    if (isPortrait) {
      window.scrollBy(0, 1);
      window.scrollBy(0, -1);
    }
  }, []);

  useEffect(() => {
    if (isCurrentBrowserIOS) {
      window.addEventListener('orientationchange', handleOrientationChange);

      return (): void => {
        window.removeEventListener('orientationchange', handleOrientationChange);
      };
    }
  }, [handleOrientationChange]);

  useEffect(() => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      CoreLobbyMessageController.targets.lobby.setOrigin('*');
      CoreLobbyMessageController.targets.lobby.setWindow(iframeRef.current.contentWindow);
    }
    return (): void => {
      CoreLobbyMessageController.targets.lobby.reset();
    };
  }, []);

  const isAndroid = useMemo(() => DeviceInfo.getPlatform().IS_ANDROID_OS, []);

  const lobbyMods = useModifiers(
    styles,
    'lobby',
    {
      isAndroid,
    },
    { withBaseClass: true }
  );

  const isLobbyOpen = useHeaderSlice.getIsMiniLobbyOpen();

  if (!isLobbyOpen) return null;

  return (
    <div className={lobbyMods} ref={iframeWrapperRef}>
      {!showMiniLobbyCloseButton ? null : (
        <IconButton
          className={styles.lobby__closeButton}
          ghost
          icon={Icons.Close}
          size={IconButtonSize.LARGE}
          onClick={() => dispatch(headerActions.setMiniLobbyOpen(false))}
        />
      )}

      <iframe
        className={styles.lobby__iframeDocument}
        loading="lazy"
        name="lobby_iframe" // is hardcoded in lobby source code to be able to detect that it's loaded in iframe (https://gitlab.gametechlabs.net/pp-live/appmobile/-/blob/master/src/app/lobby/desktop/lobby-desktop.component.ts#L613) @ 2023-12-13
        ref={iframeRef}
        src={LOBBY_URL}
        title="Lobby"
        allow="compute-pressure"
      />
    </div>
  );
};

export const LobbyConnected = (): ReactElement | null => {
  const isLobbyOpen = useHeaderSlice.getIsMiniLobbyOpen();
  const dispatch = useCoreDispatch();
  const isCrashGame = useGameConfig().config.isCrashGame;
  const isSlotsLobbyValue = useConfigSlice.getIsSlotsLobby();
  const isSlotsLobby = isCrashGame && isSlotsLobbyValue; // slots redirection is only for crash games.
  const handleSlotRedirect = useSlotsRedirectFunction(true);

  useEffect(() => {
    if (!isLobbyOpen) {
      dispatch(gameStateActions.setGameSuspended(false));
    }
  }, [isLobbyOpen, dispatch]);

  useEffect(() => {
    if (isLobbyOpen && isSlotsLobby) {
      handleSlotRedirect();
    }
  }, [isLobbyOpen, isSlotsLobby, dispatch, handleSlotRedirect]);

  if (!isLobbyOpen || isSlotsLobby) return null;

  return <LobbyBodyConnected />;
};
